import { Button, Modal } from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import axios, { AxiosError } from "axios";
import { userState } from "pages/account/content/atoms/user";
import { ChangeEvent, MouseEvent, useEffect } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import { notify } from "scripts/notify";
import { IAccount, User } from "shared/interfaces/user";
import { accountNumber } from "../../atoms/accountNumber";

import { validationError } from "../../atoms/validationError";
import { getAuthorization } from "scripts/jwt/getAuthorization";
import { Spinner } from "../../../spinner";
import { loadingState } from "pages/account/content/atoms/loadingState";
import { accountNumberSchema } from "../../schema/accountNumber.schema";

const EditModal = ({
  isOpen,
  handleClose,
  accountId,
  value,
}: {
  isOpen: boolean;
  handleClose: () => void;
  accountId: string;
  value: string;
}) => {
  const [textValue, setTextValue] = useRecoilState(accountNumber);
  const [user, setUser] = useRecoilState(userState);
  const [error, setError] = useRecoilState(validationError);
  const setLoading = useSetRecoilState(loadingState);

  useEffect(() => {
    if (isOpen) {
      setTextValue(value);
    }
  }, [isOpen, setTextValue, value]);

  const validate = (value: string) => {
    let isValidate = false;
    accountNumberSchema
      .validate({ account: value })
      .then((data) => {
        isValidate = true;
        setError("");
      })
      .catch((err) => {
        isValidate = false;
        setError(err.errors[0]);
      });

    return isValidate;
  };

  const textFieldHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    validate(value);
    setTextValue(value);
  };

  const handleSubmit: (e: MouseEvent<HTMLButtonElement>) => void = async () => {
    if (!user || error !== "" || textValue === value || textValue === "") {
      return;
    }

    const selectedAccount = user.Accounts.filter((acc) => {
      return acc.id === accountId;
    })[0];

    const api = process.env.REACT_APP_API;
    setLoading(true);
    try {
      const response = await axios.patch(
        `${api}/account`,
        {
          id: selectedAccount.id,
          number: textValue,
        },
        {
          headers: {
            Authorization: getAuthorization(),
          },
        }
      );
      const { data, status } = response;
      if (status === 200 && data === "Account Updated") {
        notify("success", data);
      }
    } catch (error) {
      setLoading(false);
      handleClose();
      const e = error as AxiosError;
      if (!e.isAxiosError) {
        notify("error", "Something Went Wrong");
        return;
      }

      const response = e.response;
      if (response?.status === 403) {
        notify("error", response.data.message);
        return;
      }
      notify("error", "Something Went Wrong");
      return;
    }

    handleClose();
    const updatedUser: User = { ...user, Accounts: [...user.Accounts] };
    const updatedSelectedAccount: IAccount = {
      id: selectedAccount.id,
      account: textValue,
      userId: selectedAccount.userId,
      status: selectedAccount.status,
    };
    updatedUser.Accounts = user.Accounts.filter((acc) => {
      return acc.id !== accountId;
    });
    updatedUser.Accounts.push(updatedSelectedAccount);
    setUser(updatedUser);
    setTextValue("");
    setLoading(false);
  };
  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
    >
      <div className="h-full flex">
        <div className="m-auto bg-white p-8">
          <div>
            <TextField
              label="Enter Account Number"
              placeholder="12487854"
              value={textValue}
              onChange={textFieldHandler}
            />
          </div>
          <div className="text-xs text-red-600">{error && error}</div>
          <div className=" justify-between flex mt-5">
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={handleSubmit}
            >
              ADD
            </Button>
            <Button variant="contained" size="small" onClick={handleClose}>
              CLOSE
            </Button>
          </div>
          <div>
            <Spinner />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export { EditModal };
